import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "../state/hooks";
import { showNotification } from "../state/slices/notificationsSlice";
import { triggerDataRefresh } from "../state/slices/navigationSlice";
import { closeJobcardManpowerModal } from "../state/slices/jobcardManpowerModalSlice";
import type { JobcardManpowerModalContext } from "../state/slices/jobcardManpowerModalSlice";
import {
  fetchJobcardManpowerAllocation,
  fetchJobcardManpowerEmployees,
  saveJobcardManpowerAllocation,
} from "../services/jobcardManpowerApi";
import type {
  JobcardManpowerAssignedLine,
  JobcardManpowerDraftTime,
  JobcardManpowerEmployee,
  JobcardManpowerJobLineItems,
  JobcardManpowerRateBasis,
} from "../types/jobcardManpower";
import {
  getCurrentMonthRateBasis,
  draftTimeFromAssignedLine,
  jobcardManpowerEmptyDraft,
  jobcardManpowerEstCost,
  jobcardManpowerHasOvertime,
  jobcardManpowerLineMinutes,
  jobcardManpowerResolveOtRate,
  jobcardManpowerSummaryFromLines,
  normalizeAssignedLine,
  recalculateAssignedLineAmounts,
  resolveRegularRateForAssignedLine,
  sumAssignedLineCosts,
} from "../utils/jobcardManpowerUtils";
import { buildJobcardDetailRows } from "../utils/jobcardManpowerDetails";

function headerRecordId(header: Record<string, unknown>): string {
  const id = header.id ?? header.Id ?? header.ID;
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

export function useJobcardManpowerAllocation(
  isOpen: boolean,
  context: JobcardManpowerModalContext | null
) {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [employees, setEmployees] = useState<JobcardManpowerEmployee[]>([]);
  const [assigned, setAssigned] = useState<JobcardManpowerAssignedLine[]>([]);
  const [drafts, setDrafts] = useState<Record<string, JobcardManpowerDraftTime>>(
    {}
  );
  const [headerMeta, setHeaderMeta] = useState<Record<string, unknown>>({});
  const [totalLabourCost, setTotalLabourCost] = useState<number | "">("");
  const [rateBasis, setRateBasis] = useState<JobcardManpowerRateBasis>(
    getCurrentMonthRateBasis
  );
  const [jobcardDetails, setJobcardDetails] = useState<Record<string, unknown>>(
    {}
  );
  const [jobcardLineItems, setJobcardLineItems] =
    useState<JobcardManpowerJobLineItems>({
      labour: [],
      spares: [],
      miscellaneous: [],
    });
  const loadSeqRef = useRef(0);
  const employeeLoadSeqRef = useRef(0);
  const reconcileKeyRef = useRef<string | null>(null);
  const initializedKeyRef = useRef<string | null>(null);

  const jobcardId = context?.jobcardId?.trim() ?? "";
  const jobcardNo = context?.jobcardNo?.trim() ?? "";
  const openKey = `${jobcardId}|${jobcardNo}`;

  const applyAllocation = useCallback((alloc: Awaited<ReturnType<typeof fetchJobcardManpowerAllocation>>) => {
    const header = (alloc.header ?? {}) as Record<string, unknown>;
    setHeaderMeta(header);
    reconcileKeyRef.current = null;
    const lines = (alloc.lines ?? []).map((l) =>
      normalizeAssignedLine(l as unknown as Record<string, unknown>)
    );
    setAssigned(lines);
    setTotalLabourCost(sumAssignedLineCosts(lines) || "");
    if (alloc.rateBasis) {
      setRateBasis(alloc.rateBasis);
    }
    if (
      alloc.jobcardDetails &&
      typeof alloc.jobcardDetails === "object" &&
      Object.keys(alloc.jobcardDetails).length > 0
    ) {
      setJobcardDetails((prev) => ({
        ...prev,
        ...(alloc.jobcardDetails as Record<string, unknown>),
      }));
    }
    if (alloc.jobcardLineItems) {
      setJobcardLineItems(alloc.jobcardLineItems);
    }
  }, []);

  const loadAllocation = useCallback(async () => {
    if (!jobcardId && !jobcardNo) return;
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const alloc = await fetchJobcardManpowerAllocation(
        jobcardId || jobcardNo,
        jobcardNo || undefined
      );
      if (seq !== loadSeqRef.current) return;
      applyAllocation(alloc);
    } catch (e: unknown) {
      if (seq !== loadSeqRef.current) return;
      const msg =
        e instanceof Error ? e.message : "Failed to load saved assignment";
      dispatch(showNotification({ message: msg, type: "error" }));
      setAssigned([]);
      setHeaderMeta({});
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
  }, [applyAllocation, dispatch, jobcardId, jobcardNo]);

  const loadEmployees = useCallback(async (searchText?: string) => {
    const seq = ++employeeLoadSeqRef.current;
    try {
      const q = typeof searchText === "string" ? searchText : search;
      const empList = await fetchJobcardManpowerEmployees(q);
      if (seq !== employeeLoadSeqRef.current) return;
      setEmployees(empList.employees);
      setRateBasis((prev) => empList.rateBasis ?? prev);
    } catch {
      /* Employee list is optional; saved assignment still shows */
    }
  }, [search]);

  useEffect(() => {
    if (!isOpen || !openKey.replace(/\|/g, "")) return;
    if (initializedKeyRef.current === openKey) return;
    initializedKeyRef.current = openKey;
    setSearch("");
    setDrafts({});
    if (context?.rowData) {
      setJobcardDetails(context.rowData);
    }
    void loadAllocation();
    void loadEmployees("");
  }, [isOpen, openKey, context, loadAllocation, loadEmployees]);

  useEffect(() => {
    if (!isOpen) return;
    const q = search;
    const t = window.setTimeout(() => {
      void loadEmployees(q);
    }, 300);
    return () => window.clearTimeout(t);
  }, [isOpen, search, loadEmployees]);

  /** After employees load, recalc saved lines (DB may have old single-rate totals). */
  useEffect(() => {
    if (!isOpen || employees.length === 0) return;
    setAssigned((prev) => {
      if (prev.length === 0) return prev;
      if (reconcileKeyRef.current === openKey) return prev;
      reconcileKeyRef.current = openKey;
      const fallback = rateBasis.default_hourly_rate || 350;
      const next = prev.map((line) =>
        recalculateAssignedLineAmounts(
          line,
          resolveRegularRateForAssignedLine(line, employees, fallback)
        )
      );
      const sum = sumAssignedLineCosts(next);
      setTotalLabourCost(sum > 0 ? sum : "");
      return next;
    });
  }, [isOpen, openKey, employees, rateBasis.default_hourly_rate]);

  useEffect(() => {
    if (!isOpen) {
      loadSeqRef.current += 1;
      employeeLoadSeqRef.current += 1;
      reconcileKeyRef.current = null;
      initializedKeyRef.current = null;
      setAssigned([]);
      setEmployees([]);
      setTotalLabourCost("");
      setHeaderMeta({});
      setJobcardDetails({});
      setJobcardLineItems({ labour: [], spares: [], miscellaneous: [] });
      setRateBasis(getCurrentMonthRateBasis());
    }
  }, [isOpen]);

  const jobDetailRows = useMemo(
    () =>
      buildJobcardDetailRows([
        context?.rowData,
        jobcardDetails,
        headerMeta,
        context
          ? {
              jobcard_no: context.jobcardNo,
              vehicle_make: context.vehicleMake,
              model: context.model,
              reg_no: context.regNo,
              customer_name: context.customerName,
            }
          : null,
      ]),
    [context, jobcardDetails, headerMeta]
  );

  const normalizeEmpId = (id: string) => String(id || "").trim().toLowerCase();

  const assignedIds = useMemo(
    () => new Set(assigned.map((a) => normalizeEmpId(a.employeeid))),
    [assigned]
  );

  const summary = useMemo(
    () => jobcardManpowerSummaryFromLines(assigned, totalLabourCost),
    [assigned, totalLabourCost]
  );

  const subtitle = useMemo(() => {
    const no =
      (headerMeta.jobcard_no as string) || context?.jobcardNo || "";
    const vehicle = [
      (headerMeta.vehicle_make as string) || context?.vehicleMake,
      (headerMeta.model as string) || context?.model,
    ]
      .filter(Boolean)
      .join(" ");
    return [no, vehicle].filter(Boolean).join(" • ");
  }, [context, headerMeta]);

  const getDraft = useCallback(
    (empId: string): JobcardManpowerDraftTime =>
      drafts[empId] ?? jobcardManpowerEmptyDraft(),
    [drafts]
  );

  const setDraft = useCallback((empId: string, next: JobcardManpowerDraftTime) => {
    setDrafts((prev) => ({ ...prev, [empId]: next }));
  }, []);

  const defaultHourlyRate = useCallback(
    (emp: JobcardManpowerEmployee) =>
      Number(emp.hourly_rate) ||
      rateBasis.default_hourly_rate ||
      350,
    [rateBasis.default_hourly_rate]
  );

  const resolveRegularRateForLine = useCallback(
    (line: JobcardManpowerAssignedLine) =>
      resolveRegularRateForAssignedLine(
        line,
        employees,
        rateBasis.default_hourly_rate || 350
      ),
    [employees, rateBasis.default_hourly_rate]
  );

  const getDraftOtRate = useCallback(
    (emp: JobcardManpowerEmployee) => {
      const d = getDraft(emp.id);
      return jobcardManpowerResolveOtRate(defaultHourlyRate(emp), d);
    },
    [defaultHourlyRate, getDraft]
  );

  const setDraftRate = useCallback(
    (empId: string, rate: number) => {
      const d = getDraft(empId);
      setDraft(empId, { ...d, hourly_rate: rate });
    },
    [getDraft, setDraft]
  );

  const handleUpdateAssignedRate = useCallback(
    (employeeid: string, rate: number) => {
      setAssigned((prev) =>
        prev.map((l) => {
          if (l.employeeid !== employeeid) return l;
          const numeric = Number(rate);
          const otRate =
            Number.isNaN(numeric) || numeric <= 0 ? 0 : numeric;
          const regRate = resolveRegularRateForLine(l);
          const draft = draftTimeFromAssignedLine(l);
          draft.hourly_rate = otRate;
          const amount = jobcardManpowerEstCost(regRate, draft);
          return {
            ...l,
            regular_hr_rate: regRate,
            man_hr_rate: otRate,
            amount,
            allocated_cost: amount,
          };
        })
      );
    },
    [resolveRegularRateForLine]
  );

  const handleAssign = useCallback(
    (emp: JobcardManpowerEmployee) => {
      const d = getDraft(emp.id);
      if (jobcardManpowerLineMinutes(d) <= 0) {
        dispatch(
          showNotification({
            message: "Enter regular or overtime time before assigning",
            type: "info",
          })
        );
        return;
      }
      if (assignedIds.has(normalizeEmpId(emp.id))) {
        dispatch(
          showNotification({
            message: "Employee already assigned",
            type: "info",
          })
        );
        return;
      }
      const regRate = defaultHourlyRate(emp);
      const hasOt = jobcardManpowerHasOvertime(d);
      const otRate = jobcardManpowerResolveOtRate(regRate, d);
      const amount = jobcardManpowerEstCost(regRate, d);
      setAssigned((prev) => [
        ...prev,
        {
          employeeid: emp.id,
          employee_name: emp.name,
          role: emp.role,
          regular_hr_rate: regRate,
          man_hr_rate: hasOt ? otRate : regRate,
          regular_hours: d.regular_hours,
          regular_minutes: d.regular_minutes,
          overtime_hours: d.overtime_hours,
          overtime_minutes: d.overtime_minutes,
          amount,
          allocated_cost: amount,
        },
      ]);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[emp.id];
        return next;
      });
    },
    [assignedIds, defaultHourlyRate, dispatch, getDraft]
  );

  const handleRemoveAssigned = useCallback((employeeid: string) => {
    setAssigned((prev) => prev.filter((a) => a.employeeid !== employeeid));
  }, []);

  const hasSavedRecord =
    headerRecordId(headerMeta).length > 0 || assigned.length > 0;

  const isEmployeeAssigned = useCallback(
    (empId: string) => assignedIds.has(normalizeEmpId(empId)),
    [assignedIds]
  );

  const handleSave = useCallback(async () => {
    if (!jobcardId && !jobcardNo) return;
    if (assigned.length === 0) {
      dispatch(
        showNotification({ message: "Assign at least one employee", type: "error" })
      );
      return;
    }
    setSaving(true);
    try {
      const resolvedJobcardId =
        String(headerMeta.jobcard_id || jobcardId || jobcardNo || "").trim();
      const manpowerHeaderId = headerRecordId(headerMeta);

      const recalculatedLines = assigned.map((l) =>
        recalculateAssignedLineAmounts(l, resolveRegularRateForLine(l))
      );
      const linesForSave = recalculatedLines.map((l) => ({
        employeeid: l.employeeid,
        regular_hr_rate: l.regular_hr_rate ?? resolveRegularRateForLine(l),
        man_hr_rate: l.man_hr_rate,
        regular_hours: l.regular_hours,
        regular_minutes: l.regular_minutes,
        overtime_hours: l.overtime_hours,
        overtime_minutes: l.overtime_minutes,
      }));
      const computedTotal = sumAssignedLineCosts(recalculatedLines);
      const manualTotal =
        totalLabourCost !== "" && Number(totalLabourCost) > 0
          ? Number(totalLabourCost)
          : undefined;

      await saveJobcardManpowerAllocation({
        jobcard_id: resolvedJobcardId,
        jobcard_no: jobcardNo || undefined,
        id: manpowerHeaderId || undefined,
        total_labour_cost: manualTotal ?? computedTotal,
        lines: linesForSave,
      });

      dispatch(
        showNotification({
          message: "Manpower allocation saved successfully",
          type: "success",
        })
      );
      dispatch(triggerDataRefresh());
      dispatch(closeJobcardManpowerModal());
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to save allocation";
      dispatch(showNotification({ message: msg, type: "error" }));
    } finally {
      setSaving(false);
    }
  }, [
    assigned,
    dispatch,
    headerMeta,
    jobcardId,
    jobcardNo,
    resolveRegularRateForLine,
    totalLabourCost,
  ]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        e.name?.toLowerCase().includes(q) ||
        (e.role || "").toLowerCase().includes(q)
    );
  }, [employees, search]);

  const refresh = useCallback(async () => {
    await loadAllocation();
    await loadEmployees(search);
  }, [loadAllocation, loadEmployees, search]);

  return {
    loading,
    saving,
    search,
    setSearch,
    employees: filteredEmployees,
    assigned,
    assignedIds,
    isEmployeeAssigned,
    headerMeta,
    totalLabourCost,
    setTotalLabourCost,
    summary,
    subtitle,
    rateBasis,
    jobDetailRows,
    jobcardLineItems,
    hasSavedRecord,
    getDraft,
    setDraft,
    getDraftOtRate,
    setDraftRate,
    resolveRegularRateForLine,
    defaultHourlyRate,
    handleUpdateAssignedRate,
    handleAssign,
    handleRemoveAssigned,
    handleSave,
    loadData: refresh,
    close: () => dispatch(closeJobcardManpowerModal()),
  };
}
